import { Inject, Injectable, NgZone } from '@angular/core';
import { classToPlain } from 'class-transformer';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as SockJS from 'sockjs-client';
import * as Stomp from 'stompjs';
import { KeycloakInit } from '../../keycloak/keycloak-init';
import { WebsocketConnectionParam } from '../model/websocket-connection-param';
import { ToastService } from './toast.service';
import { WEBSOCKET_CONFIG_TOKEN } from './tokens';
import { environment } from '../../../environments/environment';

@Injectable()
export class WebsocketClient {
  private readonly RECONNECT_BASE_MS = 1000;
  private readonly RECONNECT_MAX_MS = 30000;
  private readonly CONNECTION_LOST_TOAST_DELAY_MS = 60000;
  private websocketReconnectTries: number = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionLostTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionLostNotified: boolean = false;

  private stompClient: Stomp.Client | null = null;
  private readonly connectionProperties: WebsocketConnectionParam;
  private updateUserSubscription?: Subscription;
  private clientSubscriptions: SubscriptionsCollection = {};
  private topicSubjects: { [topic: string]: Subject<Array<any> | Record<string, any>> } = {};
  private reconnectSubscription?: Subscription;
  private websocketConnectionSubscription = new ReplaySubject<boolean>();
  websocketConnection$ = this.websocketConnectionSubscription.asObservable();
  websocketConnectionConnected$ = this.websocketConnection$.pipe(filter(connected => connected));

  constructor(private keycloak: KeycloakInit, private ngZone: NgZone, private toastService: ToastService,
    @Inject(WEBSOCKET_CONFIG_TOKEN) wsConnParams: WebsocketConnectionParam) {
    this.connectionProperties = wsConnParams;
  }


  init(): void {
    this.updateUserSubscription = this.keycloak.updateUserProfile$.subscribe(() => this.connectToWebsocket());
    this.reconnectSubscription = this.websocketConnectionConnected$.subscribe(() => this.resubscribeAllTopics());
  }

  connectToWebsocket(): void {
    this.stompClient?.disconnect(() => {});
    const ws = new SockJS(this.connectionProperties.address + this.connectionProperties.endpoint);
    const stompClient: Stomp.Client = Stomp.over(ws);
    if (!environment.production) {
      stompClient.debug = (...args: string[]) => console.debug(args.join('\n'));
    } else {
      stompClient.debug = () => {};
    }
    stompClient.heartbeat.outgoing = 10000;
    stompClient.heartbeat.incoming = 10000;
    stompClient.connect({}, (frame: Stomp.Frame) => {
      if (frame.command == 'CONNECTED') {
        if (this.connectionLostTimer != null) {
          clearTimeout(this.connectionLostTimer);
          this.connectionLostTimer = null;
        }
        if (this.connectionLostNotified) {
          this.connectionLostNotified = false;
          this.toastService.showTimedToast(
            'information', null, 'ws-connection-restored', { server: this.connectionProperties.serverName?.toUpperCase() }, 'success', 10000
          );
        }
        this.websocketReconnectTries = 0;
        this.notifyOnConnectionChanged(true);
      }
      // eslint-disable-next-line no-console
      console.info(`WS connected: ${this.connectionProperties.serverName}`);
    }, (error: string | Stomp.Frame) => {
      console.error(`[WS ${this.connectionProperties.serverName}] Disconnected at ${new Date().toISOString()}:`, error);
      this.onDisconnected();
    });
    this.stompClient = stompClient;
  }

  disconnect(): void {
    this.reconnectSubscription?.unsubscribe();
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.connectionLostTimer != null) {
      clearTimeout(this.connectionLostTimer);
      this.connectionLostTimer = null;
    }
    this.stompClient?.disconnect(() => {
      // eslint-disable-next-line no-console
      console.info(`WS disconnected: ${this.connectionProperties.serverName}`);
      this.notifyOnConnectionChanged(false);
      this.updateUserSubscription?.unsubscribe();
    });
  }

  isConnected(): boolean {
    return this.stompClient?.connected || false;
  }

  private onDisconnected(): void {
    this.notifyOnConnectionChanged(false);

    if (this.connectionLostTimer == null && !this.connectionLostNotified) {
      this.connectionLostTimer = setTimeout(() => {
        this.connectionLostTimer = null;
        this.connectionLostNotified = true;
        this.toastService.showTimedToast(
          'error', null, 'ws-connection-lost', { server: this.connectionProperties.serverName?.toUpperCase() }, 'error', 10000
        );
      }, this.CONNECTION_LOST_TOAST_DELAY_MS);
    }

    const delay = Math.min(this.RECONNECT_BASE_MS * 2 ** this.websocketReconnectTries, this.RECONNECT_MAX_MS);
    const jitter = Math.random() * 0.2 * delay;
    // eslint-disable-next-line no-console
    console.info(`[WS ${this.connectionProperties.serverName}] Reconnecting in ${Math.round(delay + jitter)}ms (attempt ${this.websocketReconnectTries + 1})`);
    this.reconnectTimer = setTimeout(() => this.retryConnection(), delay + jitter);
  }

  private retryConnection(): void {
    this.websocketReconnectTries++;
    this.connectToWebsocket();
  }

  private notifyOnConnectionChanged(connected: boolean): void {
    this.ngZone.run(() => this.websocketConnectionSubscription.next(connected));
  }

  subscribe(topic: string): Subject<Array<any> | Record<string, any>> {
    this.clientSubscriptions[topic]?.unsubscribe();

    const subject = new Subject<Array<any> | Record<string, any>>();
    this.topicSubjects[topic] = subject;

    if (this.isConnected()) {
      this.subscribeStompTopic(topic);
    }

    return subject;
  }

  private subscribeStompTopic(topic: string): void {
    this.clientSubscriptions[topic]?.unsubscribe();
    const subject = this.topicSubjects[topic];
    if (!subject) return;

    const subscription = this.stompClient?.subscribe(topic,
      (message: Stomp.Message) => subject.next(JSON.parse(message.body)),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      { Authorization: `Bearer ${this.keycloak.getToken()}` }
    );

    if (subscription) {
      this.clientSubscriptions[topic] = subscription;
    }
  }

  private resubscribeAllTopics(): void {
    Object.keys(this.topicSubjects).forEach(topic => this.subscribeStompTopic(topic));
  }

  send<T>(topic: string, message: T): void {
    this.stompClient?.send(topic,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      { Authorization: `Bearer ${this.keycloak.getToken()}` },
      JSON.stringify(classToPlain(message))
    );
  }

  unsubscribe(topic: string): void {
    this.clientSubscriptions[topic]?.unsubscribe();
    delete this.clientSubscriptions[topic];
    delete this.topicSubjects[topic];
  }

}

interface SubscriptionsCollection {
  [key: string]: Stomp.Subscription;
}
